import { useEffect, useState } from 'react';
import { Logger } from '@saviynt/common';

import { useInvertedLocalize } from '../utils/localization';

import invertedMessages from './messages/activationBanner';

interface ActivationBannerReturn {
  // Success banner props
  successBanner: {
    isVisible: boolean;
    message: string;
    onClose: () => void;
  };
  // Error banner props
  errorBanner: {
    isVisible: boolean;
    message: string;
    onClose: () => void;
  };
}

/**
 * Custom hook for managing role activation feedback banners
 * Checks sessionStorage for activation success/error data and manages banner state
 */
const useActivationBanner = (): ActivationBannerReturn => {
  const l = useInvertedLocalize(invertedMessages);

  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Check for role activation feedback from sessionStorage
  useEffect(() => {
    try {
      // Check for success data
      const successData = sessionStorage.getItem('roleActivationSuccess');

      if (successData) {
        const parsed = JSON.parse(successData) as {
          roleName: string;
          roleId: string;
          timestamp: number;
        };
        const { roleName, timestamp } = parsed;

        // Safety check: only show if recent (prevent stale data from crashes)
        const isRecent = Date.now() - timestamp < 60000; // 1 minute

        if (isRecent) {
          setSuccessMessage(
            l('Your {roleName} Role has been activated!', { roleName })
          );
          setShowSuccessBanner(true);
        }

        sessionStorage.removeItem('roleActivationSuccess'); // always clean up
      }

      // Check for error data
      const errorData = sessionStorage.getItem('roleActivationError');

      if (errorData) {
        const parsed = JSON.parse(errorData) as {
          roleName: string;
          errorMessage: string;
          timestamp: number;
        };
        const { roleName, timestamp } = parsed;

        const isRecent = Date.now() - timestamp < 60000; // 1 minute

        if (isRecent) {
          setErrorMessage(
            l("Your role {roleName} couldn't be activated.", { roleName })
          );
          setShowErrorBanner(true);
        }

        sessionStorage.removeItem('roleActivationError');
      }
    } catch (parseError) {
      Logger.error('Error processing activation feedback:', parseError);
      // Clean up corrupted data
      sessionStorage.removeItem('roleActivationSuccess');
      sessionStorage.removeItem('roleActivationError');
    }
  }, [l]);

  return {
    successBanner: {
      isVisible: showSuccessBanner,
      message: successMessage,
      onClose: () => setShowSuccessBanner(false),
    },
    errorBanner: {
      isVisible: showErrorBanner,
      message: errorMessage,
      onClose: () => setShowErrorBanner(false),
    },
  };
};

export default useActivationBanner;
